import { FC, Fragment, ReactNode, useState } from 'react';
import { graphql, useFragment } from 'react-relay';

import classnames from 'classnames';

import { SeoLink } from 'dibs-elements/exports/SeoLink';
import { Swiper } from 'dibs-pinch-zoom-swipe';
import { ViewInRoomImage } from 'dibs-view-in-room/exports/ViewInRoomImage';

import styles from '../../styles.scss';
import { SwiperItem, ItemProps } from './SwiperItem';
import { VideoIcon } from './VideoIcon';

import { ProductSwiper_item$key } from './__generated__/ProductSwiper_item.graphql';
import { showVideo } from './helpers';
import { ProductSwiper_itemSearch$key } from './__generated__/ProductSwiper_itemSearch.graphql';

type ProductSwiperProps = {
    item: ProductSwiper_item$key;
    itemSearch: ProductSwiper_itemSearch$key;
    wasHovered: boolean;
    swiperWasClicked: boolean;
    isInitiallyVisibleTile: boolean;
    arrowsVisible: boolean;
    isMobile: boolean;
    itemsPerRow: number;
    index: number;
    onPageChange: (nextPage: number) => void;
    imageProps: ItemProps;
    onSwipeStart?: () => void;
    onSwipeEnd?: () => void;
    hasSingleImage: boolean;
    videoUploadToolsIsOn?: boolean | null;
    showViewInRoom?: boolean;
    centerImage?: boolean;
};

const itemFragment = graphql`
    fragment ProductSwiper_item on Item
    @argumentDefinitions(
        photosLimit: { type: "Int", defaultValue: 3 }
        viewInRoomLimit: { type: "Int", defaultValue: 0 }
        fetchVideo: { type: "Boolean", defaultValue: false }
    ) {
        ...ViewInRoomImage_item
        id
        reviews {
            videoReview {
                status
            }
        }
        carouselPhotos: photos(limit: $photosLimit) {
            ...SwiperItem_photo
            smallPath
            masterOrZoomPath
        }
        video: videoVimeo @include(if: $fetchVideo) {
            vimeoId
            status
            ...SwiperItem_video
        }
        viewInRoomPhotos(limit: $viewInRoomLimit) {
            ...ViewInRoomImage_viewInRoomPhoto
        }
    }
`;

const itemSearchFragment = graphql`
    fragment ProductSwiper_itemSearch on ItemSearchQueryConnection {
        ...SwiperItem_itemSearch
    }
`;

export const ProductSwiper: FC<ProductSwiperProps> = ({
    item: itemRef,
    itemSearch: itemSearchRef,
    wasHovered,
    swiperWasClicked,
    isInitiallyVisibleTile,
    arrowsVisible,
    isMobile,
    index: tileIndex,
    onPageChange,
    imageProps,
    onSwipeStart,
    onSwipeEnd,
    hasSingleImage,
    videoUploadToolsIsOn = false,
    /**
     * if we want to show view in room images we also need to provide `viewInRoomLimit` graphql value to the
     * ProductSwiper_item fragment. Pass `null` to fetch all images.
     */
    showViewInRoom,
    centerImage = true,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);

    const [currentSlide, setCurrentSlide] = useState(0);

    const displayVideo = showVideo({
        videoUploadToolsIsOn,
        videoReviewStatus: item?.reviews?.videoReview?.status,
        vimeoId: item?.video?.vimeoId,
        videoStatus: item?.video?.status,
    });

    const carouselPhotos = item?.carouselPhotos || [];
    const video = displayVideo ? item?.video : null;

    const viewInRoomPhotos = item?.viewInRoomPhotos || [];
    const mainImagePaths = {
        imagePath: carouselPhotos?.[0]?.smallPath,
        masterOrZoomPath: carouselPhotos?.[0]?.masterOrZoomPath,
    };

    const imageContainerClassNames = classnames(
        hasSingleImage ? styles.imageContainer : styles.swiperImageContainer,
        styles[imageProps.imageSize],
        {
            [styles.centerImage]: centerImage,
        }
    );

    let globalIndex = 0;

    const items = carouselPhotos.reduce<ReactNode[]>((acc, photo) => {
        if (photo) {
            acc.push(
                <SwiperItem
                    {...imageProps}
                    useEagerImageLoad={
                        (isInitiallyVisibleTile && globalIndex === 0) ||
                        wasHovered ||
                        swiperWasClicked
                    }
                    photo={photo}
                    itemSearch={itemSearch}
                    // Include video ref only if it exists and item is first in carousel
                    video={globalIndex === 0 ? video : null}
                    index={globalIndex}
                    key={`product-thumb-image-${tileIndex}-${globalIndex}`}
                    containerClassName={imageContainerClassNames}
                />
            );
            globalIndex += 1;
        }

        return acc;
    }, []);

    if (showViewInRoom) {
        const link = imageProps.disableLinks
            ? { path: '', onClick: undefined }
            : { path: imageProps.pdpUrl, onClick: imageProps.onClick };

        items.push(
            ...viewInRoomPhotos.reduce<ReactNode[]>((acc, photo) => {
                if (photo && item) {
                    acc.push(
                        <div
                            key={`product-view-in-room-image-${tileIndex}-${globalIndex}`}
                            className={classnames(
                                styles.viewInRoomContainer,
                                styles[imageProps.imageSize]
                            )}
                        >
                            <SeoLink
                                dataTn="view-in-room-image-link"
                                linkData={{ path: link.path, isLinkable: false }}
                                onClick={link.onClick}
                            >
                                <ViewInRoomImage
                                    {...imageProps}
                                    useEagerImageLoad={
                                        (isInitiallyVisibleTile && globalIndex === 0) ||
                                        wasHovered ||
                                        swiperWasClicked
                                    }
                                    item={item}
                                    viewInRoomPhoto={photo}
                                    mainImagePaths={mainImagePaths}
                                    className={styles.viewInRoomImage}
                                />
                            </SeoLink>
                        </div>
                    );
                    globalIndex += 1;
                }
                return acc;
            }, [])
        );
    }

    return (
        <Fragment>
            {video && <VideoIcon hidden={currentSlide > 0} />}
            {hasSingleImage ? (
                items[0]
            ) : (
                <Swiper
                    itemsPerPage={1}
                    showArrows={!isMobile}
                    hasInfiniteLoop
                    onPageChange={(page: number) => {
                        setCurrentSlide(page);
                        onPageChange(page);
                    }}
                    classNames={{
                        item: styles.swiperImageListItem,
                        prevArrow: [
                            styles.bumpSpecificity,
                            styles.swiperArrow,
                            styles.leftArrow,
                            arrowsVisible ? styles.arrowVisible : null,
                        ],
                        nextArrow: [
                            styles.bumpSpecificity,
                            styles.swiperArrow,
                            styles.rightArrow,
                            arrowsVisible ? styles.arrowVisible : null,
                        ],
                    }}
                    onSwipeStart={onSwipeStart}
                    onSwipeEnd={onSwipeEnd}
                >
                    {items}
                </Swiper>
            )}
        </Fragment>
    );
};
